import { useState, useEffect } from 'react';
import { episodes } from '../../mocks/episodes';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showAllEpisodes, setShowAllEpisodes] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const visibleEpisodes = showAllEpisodes ? episodes : episodes.slice(0, 8);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img 
              src="https://static.readdy.ai/image/f4233a7571349c334c07289682befdf5/e68a3005bc6871eb6581c8691c0d3c19.jpeg" 
              alt="Fala Pública Logo" 
              className="w-12 h-12 object-cover rounded-full"
            />
            <span className={`text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              Fala Pública
            </span>
          </div>

          <div className="flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('home')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}
            >
              Início
            </button>
            <button 
              onClick={() => scrollToSection('sobre')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}
            >
              Sobre
            </button>
            <button 
              onClick={() => scrollToSection('episodios')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}
            >
              Episódios
            </button>
            <button 
              onClick={() => scrollToSection('equipe')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}
            >
              Equipe
            </button>
            <button 
              onClick={() => scrollToSection('contato')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}
            >
              Contato
            </button>

            <div className="flex items-center gap-3 ml-4">
              <a 
                href="https://www.instagram.com/falapublicapodcast" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                  isScrolled ? 'bg-teal-600 hover:bg-teal-700' : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <i className="ri-instagram-line text-white text-lg"></i>
              </a>
              <a 
                href="https://www.youtube.com/@PodcastFalaPublica" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                  isScrolled ? 'bg-teal-600 hover:bg-teal-700' : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <i className="ri-youtube-line text-white text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://static.readdy.ai/image/f4233a7571349c334c07289682befdf5/2e2cd36f742e8ae5f684744c7f313fbd.jpeg" 
            alt="Podcast Studio" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="inline-block bg-teal-500/30 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-sm font-medium text-white">Podcast sobre Políticas Públicas</span>
            </div>

            <h1 className="text-6xl font-bold mb-6 leading-tight">
              Fala Pública<br />
              <span className="text-teal-300">Conversas que</span><br />
              Transformam Políticas
            </h1>

            <p className="text-lg text-gray-200 mb-8 leading-relaxed">
              Democratizando o debate sobre políticas públicas através de conversas acessíveis, 
              informativas e transformadoras com especialistas e cidadãos engajados.
            </p>

            <div className="flex gap-4">
              <a 
                href="https://www.youtube.com/@PodcastFalaPublica" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-xl"></i>
                Ouvir Último Episódio
              </a>
              <button 
                onClick={() => scrollToSection('sobre')}
                className="bg-transparent border-2 border-white hover:bg-white/10 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
              >
                Conhecer Mais
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <div className="bg-teal-800 text-white p-10 rounded-2xl shadow-lg">
                <p className="text-sm text-teal-200 mb-4">Nosso Propósito</p>
                <blockquote className="text-2xl font-semibold mb-8 leading-relaxed">
                  "Democratizar o debate sobre políticas públicas através de conversas acessíveis e informativas"
                </blockquote>
                <div className="flex items-center gap-6 pt-6 border-t border-teal-700">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
                    <i className="ri-mic-line text-3xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-teal-200">Desde 2023</p>
                    <p className="text-xl font-bold">100+ Episódios</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <p className="text-sm text-gray-500 mb-3 uppercase tracking-wide">Sobre o Fala Pública</p>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Conectando<br />
                Cidadãos e<br />
                Políticas Públicas
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  O Fala Pública nasceu da necessidade de tornar as discussões sobre políticas públicas 
                  mais acessíveis e compreensíveis para todos os cidadãos. Acreditamos que o conhecimento 
                  sobre como as políticas afetam nossas vidas deve estar ao alcance de todos.
                </p>
                <p>
                  Através de entrevistas com especialistas, gestores públicos e cidadãos engajados, 
                  exploramos temas que vão desde saúde e educação até meio ambiente e economia, 
                  sempre com uma linguagem clara e objetiva.
                </p>
                <p>
                  Nossa missão é criar uma ponte entre o conhecimento técnico e a população, 
                  promovendo o debate qualificado e incentivando a participação cidadã nas 
                  decisões que moldam nossa sociedade.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Episodes Section */}
      <section id="episodios" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-sm text-teal-600 mb-2 uppercase tracking-wide font-medium">Do mais recente ao mais antigo</p>
              <h2 className="text-5xl font-bold text-gray-900 mb-3">Episódios Recentes</h2>
              <p className="text-gray-500">Os últimos episódios do canal Fala Pública no YouTube</p>
            </div>
            <a 
              href="https://www.youtube.com/@PodcastFalaPublica/videos" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Ver canal completo
              <i className="ri-youtube-line text-xl"></i>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            {visibleEpisodes.map((episode) => (
              <a 
                key={episode.id} 
                href={episode.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group block cursor-pointer"
              >
                <div className="relative w-full h-0 pb-[56.25%] rounded-xl overflow-hidden mb-3 bg-gray-100">
                  <img 
                    src={episode.image} 
                    alt={episode.title} 
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                      <i className="ri-play-fill text-teal-700 text-xl ml-0.5"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                    {episode.duration}
                  </div>
                  <div className="absolute top-2 left-2 bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                    EP.{episode.id}
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1.5 group-hover:text-teal-700 transition-colors line-clamp-2">
                  {episode.title}
                </h3>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {episode.views}
                  </span>
                  <span>•</span>
                  <span>{episode.date}</span>
                </div>
              </a>
            ))}
          </div>

          <div className="flex flex-col items-center gap-4">
            {!showAllEpisodes && episodes.length > 8 && (
              <button 
                onClick={() => setShowAllEpisodes(true)}
                className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Mostrar mais episódios
                <i className="ri-arrow-down-line"></i>
              </button>
            )}
            <a 
              href="https://www.youtube.com/@PodcastFalaPublica" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-youtube-line text-lg"></i>
              Ver Todos os Episódios no YouTube
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="equipe" className="py-24 bg-amber-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-gray-500 mb-3 uppercase tracking-wide">Nossa Equipe</p>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Vozes por Trás<br />do Microfone
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Conheça os apresentadores que tornam o Fala Pública uma realidade, 
              trazendo conteúdo de qualidade sobre políticas públicas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {[
              {
                name: 'Pietro Bernardes',
                role: 'Apresentador',
                bio: 'Comunicador apaixonado por políticas públicas e transformação social através do diálogo.',
                image: 'https://static.readdy.ai/image/f4233a7571349c334c07289682befdf5/42ab2742e9f96549d07664b289c11693.jpeg',
                instagram: 'https://www.instagram.com/pietrobernardes_'
              },
              {
                name: 'Flávia Aguiar',
                role: 'Apresentadora',
                bio: 'Especialista em comunicação e debates sobre políticas públicas, trazendo clareza aos temas complexos.',
                image: 'https://static.readdy.ai/image/f4233a7571349c334c07289682befdf5/d7ef793f7f3f00722a2db28fb164fca3.png',
                instagram: 'https://www.instagram.com/flavinhaguiar'
              }
            ].map((member, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden ring-4 ring-teal-100">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-teal-600 font-medium mb-4 text-lg">{member.role}</p>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">{member.bio}</p>
                <div className="flex justify-center gap-3">
                  <a 
                    href={member.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 text-white rounded-full flex items-center justify-center transition-opacity cursor-pointer"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-gray-500 mb-3 uppercase tracking-wide">Por Que Nos Ouvir</p>
            <h2 className="text-5xl font-bold text-gray-900">
              Conteúdo Que Faz<br />A Diferença
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-voice-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Especialistas Renomados</h3>
              <p className="text-gray-600 leading-relaxed">
                Conversas com profissionais experientes e reconhecidos nas áreas de políticas públicas, 
                trazendo conhecimento técnico e prático.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-chat-3-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Linguagem Acessível</h3>
              <p className="text-gray-600 leading-relaxed">
                Transformamos temas complexos em conversas compreensíveis, sem perder a profundidade 
                e a qualidade da informação.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Impacto Mensurável</h3>
              <p className="text-gray-600 leading-relaxed">
                Nosso conteúdo já alcançou milhares de pessoas, promovendo debates qualificados 
                e incentivando a participação cidadã.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Entre em Contato</h2>
            <p className="text-gray-600 text-lg">
              Tem alguma sugestão de pauta, quer participar do podcast ou fazer uma parceria? 
              Envie sua mensagem!
            </p>
          </div>

          <form 
            id="contact-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d65p9ccc5bvcefa8qq6g" 
            method="POST"
            className="bg-white rounded-2xl shadow-lg p-8"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validação
              const nome = formData.get('nome') as string;
              const email = formData.get('email') as string;
              const assunto = formData.get('assunto') as string;
              const mensagem = formData.get('mensagem') as string;
              
              if (!nome || !email || !mensagem) {
                alert('Por favor, preencha todos os campos obrigatórios.');
                return;
              }
              
              if (mensagem.length > 500) {
                alert('A mensagem não pode exceder 500 caracteres.');
                return;
              }
              
              // Criar corpo do e-mail
              const emailBody = `Nome: ${nome}%0D%0AE-mail: ${email}%0D%0AAssunto: ${assunto}%0D%0A%0D%0AMensagem:%0D%0A${encodeURIComponent(mensagem)}`;
              
              // Abrir cliente de e-mail
              window.location.href = `mailto:falapublicapodcast@gmail.com?subject=${encodeURIComponent(assunto)}&body=${emailBody}`;
              
              // Limpar formulário
              form.reset();
              const counter = document.getElementById('char-counter');
              if (counter) {
                counter.textContent = '500 caracteres restantes';
              }
            }}
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="assunto" className="block text-sm font-medium text-gray-700 mb-2">
                Assunto
              </label>
              <select
                id="assunto"
                name="assunto"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
              >
                <option value="Sugestão de Pauta">Sugestão de Pauta</option>
                <option value="Participação no Podcast">Participação no Podcast</option>
                <option value="Parceria">Parceria</option>
                <option value="Feedback">Feedback</option>
                <option value="Outro">Outro</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem * (máximo 500 caracteres)
              </label>
              <textarea
                id="mensagem"
                name="mensagem"
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="Escreva sua mensagem aqui..."
                onChange={(e) => {
                  const remaining = 500 - e.target.value.length;
                  const counter = document.getElementById('char-counter');
                  if (counter) {
                    counter.textContent = `${remaining} caracteres restantes`;
                  }
                }}
              ></textarea>
              <p id="char-counter" className="text-sm text-gray-500 mt-1">500 caracteres restantes</p>
            </div>

            <button
              type="submit"
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-4 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line"></i>
              Enviar Mensagem
            </button>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 rounded-2xl overflow-hidden shadow-2xl">
            <div className="relative bg-teal-800 p-12 flex flex-col justify-center">
              <div className="absolute inset-0 opacity-20">
                <img 
                  src="https://public.readdy.ai/ai/img_res/b280111ed1a4b560a23874a93f84e8f0.jpg" 
                  alt="Discussion" 
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="relative z-10 text-white">
                <p className="text-sm text-teal-200 mb-4 uppercase tracking-wide">Junte-se à Conversa</p>
                <h2 className="text-4xl font-bold mb-6 leading-tight">
                  Toda Semana<br />
                  Novos Episódios<br />
                  Novas Perspectivas
                </h2>
              </div>
            </div>

            <div className="bg-white p-12 flex flex-col justify-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                Inscreva-se na<br />Nossa Newsletter
              </h3>
              <p className="text-gray-600 text-center mb-8">
                Receba resumos semanais dos episódios e conteúdos exclusivos sobre políticas públicas
              </p>
              <form 
                id="newsletter-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d65p9ccc5bvcefa8qq6g" 
                method="POST"
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  const email = formData.get('email') as string;
                  
                  if (!email) {
                    alert('Por favor, insira seu e-mail.');
                    return;
                  }
                  
                  fetch('https://readdy.ai/api/form/d65p9ccc5bvcefa8qq6g', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Inscrição realizada com sucesso!');
                      form.reset();
                    } else {
                      alert('Erro ao realizar inscrição. Tente novamente.');
                    }
                  })
                  .catch(() => {
                    alert('Erro ao realizar inscrição. Tente novamente.');
                  });
                }}
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Seu melhor e-mail"
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                />
                <button
                  type="submit"
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-4 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Inscrever
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
              <p className="text-sm text-gray-500 text-center mt-4">
                Sem spam. Cancele quando quiser.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-900 text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1200 400">
            <path d="M0,200 Q300,100 600,200 T1200,200" stroke="currentColor" fill="none" strokeWidth="2"/>
            <path d="M0,250 Q300,150 600,250 T1200,250" stroke="currentColor" fill="none" strokeWidth="2"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/f4233a7571349c334c07289682befdf5/e68a3005bc6871eb6581c8691c0d3c19.jpeg" 
                  alt="Fala Pública Logo" 
                  className="w-12 h-12 object-cover rounded-full"
                />
                <span className="text-xl font-bold">Fala Pública</span>
              </div>
              <p className="text-teal-200 text-sm leading-relaxed mb-6">
                Democratizando o debate sobre políticas públicas através de conversas transformadoras.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Navegação</h4>
              <ul className="space-y-2 text-teal-200">
                <li>
                  <button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors cursor-pointer">
                    Início
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('sobre')} className="hover:text-white transition-colors cursor-pointer">
                    Sobre
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('episodios')} className="hover:text-white transition-colors cursor-pointer">
                    Episódios
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('equipe')} className="hover:text-white transition-colors cursor-pointer">
                    Equipe
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Plataformas</h4>
              <ul className="space-y-2 text-teal-200">
                <li>
                  <a href="https://www.instagram.com/falapublicapodcast" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                    Instagram
                  </a>
                </li>
                <li>
                  <a href="https://www.youtube.com/@PodcastFalaPublica" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                    YouTube
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Contato</h4>
              <ul className="space-y-2 text-teal-200">
                <li>
                  <button onClick={() => scrollToSection('contato')} className="hover:text-white transition-colors cursor-pointer">
                    Formulário
                  </button>
                </li>
                <li>
                  <a href="mailto:contato@falapublica.com" className="hover:text-white transition-colors cursor-pointer">
                    E-mail
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors cursor-pointer">
                    Parcerias
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-teal-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-teal-200 text-sm">
              © 2025 Fala Pública Podcast. Todos os direitos reservados.
            </p>

            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/falapublicapodcast" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border-2 border-teal-700 hover:border-white rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a 
                href="https://www.youtube.com/@PodcastFalaPublica" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border-2 border-teal-700 hover:border-white rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-youtube-line text-lg"></i>
              </a>
            </div>

            <div className="flex gap-6 text-teal-200 text-sm">
              <a href="#" className="hover:text-white transition-colors cursor-pointer">
                Privacidade
              </a>
              <a href="#" className="hover:text-white transition-colors cursor-pointer">
                Termos
              </a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
